import { sanitizeUrl } from '@libs/core';
import { isCssColor } from '../../utils/colorUtils';

export const getHref = (name?: string) => (name ? `${process.env.NX_PUBLIC_CLOUD_PAYSEND_API}/consumer3/svg/${name}.svg#symb` : undefined);

const isUrlOrPath = (value: string): boolean => {
    return /[^a-zA-Z0-9-_]/.test(value);
};

export const getBackgroundHref = (name?: string) => {
    if (!name) {
        return undefined;
    }
    if (isUrlOrPath(name)) {
        return sanitizeUrl(name);
    }
    return `${process.env.NX_PUBLIC_CLOUD_PAYSEND_API}/consumer3/mobile/${name}.png`;
};

// Holds a strong reference to every preloaded icon: a garbage-collected Image can have its in-flight request dropped.
const preloadedIcons = new Map<string, HTMLImageElement>();

/**
 * Warms the browser HTTP cache with an icon so it still renders when the network is gone.
 * Loads the exact URL CoreImageView uses, so the cached entry is reused as-is (no fetch/Cache API involved).
 * A failed attempt is forgotten so a later call can retry it.
 *
 * @param name icon name as passed to getHref (e.g. 'ic_alert_circle_small')
 */
export const preloadIcon = (name?: string) => {
    const href = getHref(name);
    if (!href || typeof window === 'undefined' || preloadedIcons.has(href)) {
        return;
    }
    const image = new window.Image();
    preloadedIcons.set(href, image);
    image.onerror = () => preloadedIcons.delete(href);
    image.src = href;
};

/**
 * A CSS custom property name, with or without the leading `--` (e.g. `purple_100`, `--fill-primary`).
 * `/` is allowed because design tokens arrive path-like (`fill/primary`); it is normalized to `-`.
 */
const CSS_VAR_NAME = /^(?:--)?[a-z_][a-z0-9_/-]*$/i;

/**
 * Normalizes a color option into a value usable in CSS.
 *
 * A color can only arrive as hex or `rgb()`/`rgba()` — anything else is treated as a CSS variable name:
 *
 * - `#fff`, `rgba(0, 0, 0, 0.5)` → returned unchanged;
 * - an already wrapped `var(…)` → returned unchanged;
 * - `purple_100` → `var(--purple_100)`, `--fill-primary` → `var(--fill-primary)`, `red` → `var(--red)`;
 * - a path-like token name has its `/` replaced with `-`: `fill/primary` → `var(--fill-primary)`;
 * - an empty value, or a name that is not a valid custom property ident, yields `undefined` so the
 *   caller can fall back instead of emitting a broken declaration.
 *
 * @param value color or CSS variable name coming from widget options
 */
export const resolveCssColor = (value?: string): string | undefined => {
    const normalized = value?.trim();
    if (!normalized) {
        return undefined;
    }
    if (isCssColor(normalized)) {
        return normalized;
    }
    if (/^var\(--[a-z0-9_-]+(?:\s*,[^;{}]*)?\)$/i.test(normalized)) {
        return normalized;
    }
    if (!CSS_VAR_NAME.test(normalized)) {
        return undefined;
    }
    const name = normalized.replace(/\//g, '-');
    return name.startsWith('--') ? `var(${name})` : `var(--${name})`;
};
